import React from 'react';
import { RefreshCw, ArrowLeft } from 'lucide-react';
import i18n from '../../i18n';

/**
 * Per-page error boundary — catches render errors in a single lazy route
 * without taking down the whole app. Shows a recovery UI with a back button
 * so the user can navigate away rather than having to reload the entire app.
 */
class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[PageErrorBoundary] Page render error:', error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
          <span className="text-3xl">⚠️</span>
        </div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">
          {i18n.t('errorBoundary.pageTitle', 'This page ran into a problem')}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mb-6">
          {i18n.t('errorBoundary.pageDescription', 'Something went wrong loading this page. Your data is safe — you can go back or reload.')}
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ArrowLeft size={15} />
            {i18n.t('errorBoundary.goBack', 'Go Back')}
          </button>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-800 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
          >
            <RefreshCw size={15} />
            {i18n.t('errorBoundary.tryAgain', 'Try Again')}
          </button>
        </div>
        {import.meta.env.DEV && this.state.error && (
          <pre className="mt-6 text-left text-xs text-red-500 bg-red-50 dark:bg-red-900/20 p-4 rounded-xl max-w-lg overflow-auto w-full">
            {this.state.error.toString()}
          </pre>
        )}
      </div>
    );
  }
}

export default PageErrorBoundary;
